// Einbehalt-Betrag an der Antwortoption (`cl_options.retention_cost`).
//
// Diese Datei ist die Client-Hälfte einer Regel, die es zweimal gibt. Die
// Quelle ist das Backend: `app/api/helper/checklist/retention_cost.js` in
// `8p2-cld-backend` (`checkRetentionCost`, `formatRetentionCost`). Der Server
// weist einen ungültigen Betrag ab, bevor er speichert — auch den
// Designer-Payload (`updateAnswer`).
//
// Warum die Regel hier trotzdem noch einmal steht: ohne sie tippt der Anwender
// einen Betrag, drückt Speichern und bekommt einen deutschen Servertext erst
// nach dem Absenden zu sehen. Die Maske muss deshalb genau das zulassen, was
// der Transport zulässt, nicht mehr und nicht weniger. Der Server bleibt die
// Absicherung, diese Datei ist die Fehlermeldung.
//
// Ändert sich die Regel, ändert sie sich in beiden Repos im selben Zug. Die
// Deckungsgleichheit der akzeptierten Menge wird gegen das Backend-Modul
// gemessen, nicht nacherzählt; die Erwartungstabelle in
// `tests/unit/RetentionCostRule.spec.ts` stammt aus so einem Lauf.
//
// TRENNERREGEL (Wortlaut aus der Backend-Quelle):
//
//   Kommen `,` und `.` gemeinsam vor, ist der ZULETZT stehende der
//   Dezimaltrenner und der andere Tausendertrenner. Kommt nur einer vor, ist er
//   Dezimaltrenner, wenn er EINMAL vorkommt, und Tausendertrenner, wenn er
//   MEHRFACH vorkommt.
//
// MEHRDEUTIGKEITS-SPERRE: ein einzelner Trenner mit genau drei Ziffern dahinter
// und 1-3 Ziffern ohne führende Null davor (`1.250`, `123,500`) wird
// abgewiesen, weil die beiden Lesarten dort um den Faktor 1000 auseinander
// liegen. Sie bleibt auch hier bestehen, obwohl unter dem Eingabefeld ein Echo
// des normalisierten Betrages steht: der zweite Schreibweg derselben Regel (der
// CSV-Import) hat keines, und die Maske darf nicht großzügiger sein als der
// Transport.
//
// `0` ist ein gültiger, setzbarer Wert, und leer ist erlaubt. Deshalb darf
// weder hier noch beim Aufrufer auf Truthiness geprüft werden.

/**
 * Praefix, an dem der Client eine Wert-Abweisung des Servers ERKENNT. Quelle:
 * `retentionCostReasonText` im Backend-Modul - alle Gruende erzeugen einen Text,
 * der damit beginnt.
 *
 * Erkannt wird an der Gestalt der Meldung und NICHT am HTTP-Status: das `.catch`
 * im Server-Controller bildet jede Ablehnung auf 400 ab, auch Mongo- und
 * Infrastrukturfehler mit rohem `err.message`. Wer daraus einen
 * `status === 400`-Zweig baut, zeigt dem Anwender einen Verbindungsfehler als
 * Anwendertext und nimmt ihm den Wiederholungsknopf genau dort weg, wo der
 * Speichervorgang wirklich verloren ist. Deshalb Weiss-Liste, keine
 * Schwarz-Liste.
 */
export const RETENTION_COST_MESSAGE_PREFIX = "Monetäre Bewertung ";

/** Genau die Menge, in der die beiden Lesarten um 1000 auseinanderliegen. */
const MEHRDEUTIG = /^[1-9][0-9]{0,2}[.,][0-9]{3}$/;

/**
 * Prüft einen Rohwert für `retention_cost`.
 *
 * @param {*} raw Rohwert aus dem Eingabefeld oder aus dem geladenen Datensatz.
 * @returns {{ok: true, value: (number|null)}|{ok: false, reason: string}}
 *   `ok: true` mit `value === null` heißt "leer" (erlaubt), mit einer Zahl
 *   heißt "gültig". `ok: false` trägt `reason` aus
 *   `"negativ" | "keine_zahl" | "mehrdeutig"`.
 */
export function checkRetentionCost(raw) {
  // 1. Leer erkennen, bevor irgendetwas anderes passiert. Alles, was WEITER
  //    UNTEN `null` ergäbe, ist eine Abweisung und kein "leer".
  if (raw === undefined || raw === null) return { ok: true, value: null };

  // 2. Bereits eine Zahl: nur die Endlichkeit entscheidet. Das Vorzeichen
  //    prüft Schritt 10, damit `-50` und `"-50"` dieselbe Antwort bekommen.
  if (typeof raw === "number") {
    if (!Number.isFinite(raw)) return { ok: false, reason: "keine_zahl" };
    return raw < 0 ? { ok: false, reason: "negativ" } : { ok: true, value: raw };
  }

  // 3. Sonst als Text lesen; leerer Text ist "leer".
  let s = String(raw).trim();
  if (s === "") return { ok: true, value: null };

  // 4. Alles außer Ziffern, Trennern und Minus fällt weg (damit auch `€`,
  //    NBSP und schmale Leerzeichen).
  s = s.replace(/[^0-9.,-]/g, "");

  // 5. Vorzeichen merken, dann alle Minus entfernen.
  const negative = s.charAt(0) === "-";
  s = s.replace(/-/g, "");

  // 6. Ohne Ziffer ist es keine Zahl. Ohne diese Prüfung liefert `Number("")`
  //    die Zahl `0` — `abc` wäre dann ein Einbehalt von 0 Euro.
  if (!/[0-9]/.test(s)) return { ok: false, reason: "keine_zahl" };

  // 7. Mehrdeutigkeits-Sperre (siehe Kopf).
  if (MEHRDEUTIG.test(s)) return { ok: false, reason: "mehrdeutig" };

  // 8. Trennerregel.
  const kommas = (s.match(/,/g) || []).length;
  const punkte = (s.match(/\./g) || []).length;
  let dezimal = null;
  if (kommas > 0 && punkte > 0) {
    dezimal = s.lastIndexOf(",") > s.lastIndexOf(".") ? "," : ".";
  } else if (kommas === 1) {
    dezimal = ",";
  } else if (punkte === 1) {
    dezimal = ".";
  }

  if (dezimal === ",") {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (dezimal === ".") {
    s = s.replace(/,/g, "");
  } else {
    s = s.replace(/[.,]/g, "");
  }

  // 9. Jetzt muss eine endliche Zahl herauskommen.
  const n = Number(s);
  if (!Number.isFinite(n)) return { ok: false, reason: "keine_zahl" };

  // 10. Vorzeichen anwenden. `-0` ist nicht `< 0` und ergibt damit `0` — die
  //     Null wird dabei ohne Vorzeichen zurückgegeben, damit "0" und "-0"
  //     nicht zwei Gestalten desselben Betrages werden.
  const value = negative ? -n : n;
  if (value < 0) return { ok: false, reason: "negativ" };
  return { ok: true, value: value === 0 ? 0 : value };
}

/**
 * Darstellungsform eines gespeicherten Wertes für das Eingabefeld. Der Server
 * liefert `retention_cost` bereits als String aus, wenn das Feld am Dokument
 * überhaupt vorhanden ist — für jedes Dokument ohne das Feld kommt hier
 * `undefined` an, und daraus muss ein leerer String werden.
 *
 * Der Rückgabewert ist IMMER ein String: das Feld geht als String auf die
 * Leitung, nie als JSON-Zahl (eine vorliegende Zahl nimmt der Server in
 * Schritt 2 unbesehen an, denselben Betrag als Text kann Schritt 7 abweisen).
 *
 * @param {*} v Wert des Attributs `retention_cost` (String, Zahl, `null` oder fehlend).
 * @returns {string}
 */
export function formatRetentionCost(v) {
  if (v === undefined || v === null || v === "") return "";
  return String(v);
}

/**
 * Echo des normalisierten Betrages unter dem Eingabefeld. Leer und ungültig
 * ergeben beide einen leeren String — der ungültige Fall trägt bereits die
 * Fehlermeldung der Regel und braucht kein zweites Echo.
 *
 * @param {*} raw Rohwert aus dem Eingabefeld.
 * @returns {string}
 */
export function retentionCostEcho(raw) {
  const res = checkRetentionCost(raw);
  if (!res.ok || res.value === null) return "";
  return String(res.value);
}
